import { ApiErrorPageWithTrans } from '@components/Error/ApiErrorPageWithTrans';
import { ContentWrapperCustomerApp } from '@components/Layouts/ContentWrapperCustomerApp';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { TireQuoteRecordCustomer } from '@navio/nsdk';
import { noddiAsync, URLKeys } from 'noddi-async';
import { NoddiEmptyContent, NoddiLink, useNoddiQueryParam } from 'noddi-ui-web';
import { customerRoutes } from 'noddi-util';
import { useAuthContext } from 'noddi-web';
import { useEffect, useMemo, useState } from 'react';
import { StringParam, withDefault } from 'use-query-params';

import { useTireOfferPickerStore } from '../../../stores/tireOfferPickerStore';
import { BulkBar } from './components/BulkBar';
import { ConfirmBatchModal } from './components/ConfirmBatchModal';
import { DeclineModal } from './components/DeclineModal';
import { StatStrip } from './components/StatStrip';
import { TireOfferHomeSkeleton } from './components/TireOfferHomeSkeleton';
import { TireOfferTabsSection } from './components/TireOfferTabsSection';
import { applyStrategyToOffers, getOfferTotalForOption } from './utils/applyStrategy';
import { getTireOfferStats } from './utils/getTireOfferStats';
import {
  filterSelectionByOffers,
  getOfferBuckets,
  getOfferSelections,
  sortAcceptedOffers,
  sortPendingOfferList
} from './utils/offerData';
import { isTabKey, TabKey } from './utils/tabKeys';

export const TireOfferHomePage = () => {
  const { currentUserGroupId: userGroupId } = useAuthContext();

  const [tabRaw, setTabRaw] = useNoddiQueryParam('tab', withDefault(StringParam, 'pending'));
  const activeTab: TabKey = isTabKey(tabRaw) ? tabRaw : 'pending';

  const mode = useTireOfferPickerStore((state) => state.mode);
  const bulkStrategy = useTireOfferPickerStore((state) => state.bulkStrategy);
  const selectedQuoteItemId = useTireOfferPickerStore((state) => state.selectedQuoteItemId);
  const setMode = useTireOfferPickerStore((state) => state.setMode);
  const setSelection = useTireOfferPickerStore((state) => state.setSelection);
  const clearSelection = useTireOfferPickerStore((state) => state.clearSelection);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [declineOpen, setDeclineOpen] = useState(false);

  const {
    data,
    isPending: isPendingOffers,
    isFetching: isFetchingOffers,
    error: errorOffers
  } = noddiAsync.useGet({
    type: URLKeys.getCarTireQuotesIgnoringImpersonation,
    input: { userGroupIds: userGroupId == null ? [] : [userGroupId] }
  });

  const offers = useMemo(() => data?.results ?? [], [data?.results]);

  const buckets = useMemo(() => getOfferBuckets(offers), [offers]);
  const sortedPendingList = useMemo(() => sortPendingOfferList(buckets.pending), [buckets.pending]);
  const acceptedOffers = useMemo(() => sortAcceptedOffers(buckets.accepted), [buckets.accepted]);

  // Fetch full quote details for pending offers so we have quoteItems for the inline picker.
  const pendingDetailQueries = noddiAsync.useGetAll(
    sortedPendingList.map((offer) => ({
      type: URLKeys.getCarTireQuoteBySlug,
      input: { carTireQuoteSlug: offer.slug }
    }))
  );

  const pendingOffers = useMemo<TireQuoteRecordCustomer[]>(
    () =>
      pendingDetailQueries
        .map((query) => query.data)
        .filter((value): value is TireQuoteRecordCustomer => value != null),
    [pendingDetailQueries]
  );

  // Auto-pick mode: re-apply strategy when pending list, mode, or strategy changes.
  useEffect(() => {
    if (mode === 'auto') {
      setSelection(applyStrategyToOffers(pendingOffers, bulkStrategy));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- pendingOffers keyed by length: useGetAll returns new arrays each render, so the full value would refire setSelection forever
  }, [mode, bulkStrategy, pendingOffers.length]);

  // Drop selections for offers that no longer exist (e.g. after refetch).
  useEffect(() => {
    const next = filterSelectionByOffers(pendingOffers, selectedQuoteItemId);
    if (Object.keys(next).length !== Object.keys(selectedQuoteItemId).length) {
      setSelection(next);
    }
  }, [pendingOffers, selectedQuoteItemId, setSelection]);

  const selections = useMemo(
    () => getOfferSelections(pendingOffers, selectedQuoteItemId),
    [pendingOffers, selectedQuoteItemId]
  );

  const bulkTotal = selections.reduce((sum, { offer, quoteItem }) => sum + getOfferTotalForOption(offer, quoteItem), 0);
  const bulkCurrency = selections[0]?.quoteItem.quoteItemTires[0]?.tireInventory.salesItem.price.priceGross.currency;

  const handleSwitchMode = (next: typeof mode) => {
    if (next === mode) {
      return;
    }
    if (next === 'manual') {
      clearSelection();
    }
    setMode(next);
  };
  const handleTabChange = (nextTab: TabKey) => setTabRaw(nextTab);

  const isLoadingDetails = pendingDetailQueries.some((query) => query.isPending) && sortedPendingList.length > 0;

  if (isPendingOffers || (isFetchingOffers && !data) || isLoadingDetails) {
    return (
      <ContentWrapperCustomerApp hideBackButton title={t`Tire offers`} width='wide'>
        <TireOfferHomeSkeleton />
      </ContentWrapperCustomerApp>
    );
  }

  if (errorOffers) {
    return <ApiErrorPageWithTrans error={errorOffers} />;
  }

  if (offers.length === 0) {
    return (
      <ContentWrapperCustomerApp title={t`Tire offers`} width='wide' hideBackButton>
        <NoddiEmptyContent
          emptyIcon='Mail'
          title={t`No tire offers yet`}
          message={t`Request an offer and we'll match the right tires to your fleet.`}
        >
          <NoddiLink to={customerRoutes.requestTireOffer.getPath()} variant='primary' endIcon='ArrowRight'>
            <Trans>Request tire offer</Trans>
          </NoddiLink>
        </NoddiEmptyContent>
      </ContentWrapperCustomerApp>
    );
  }

  const stats = getTireOfferStats({
    activeTab,
    acceptedOffers,
    pendingOffers,
    pendingCount: sortedPendingList.length,
    pendingValue: bulkTotal,
    selectedCount: selections.length,
    currency: bulkCurrency
  });

  return (
    <ContentWrapperCustomerApp title={t`Tire offers`} width='wide' hideBackButton>
      <div className='flex flex-col gap-5'>
        <StatStrip stats={stats} />

        <TireOfferTabsSection
          activeTab={activeTab}
          onTabChange={handleTabChange}
          mode={mode}
          onModeChange={handleSwitchMode}
          pendingOffers={pendingOffers}
          pendingCount={sortedPendingList.length}
          acceptedOffers={acceptedOffers}
        />

        <div className='h-20' />
      </div>

      {activeTab === 'pending' && (
        <BulkBar
          count={selections.length}
          total={bulkTotal}
          currency={bulkCurrency}
          onClear={clearSelection}
          onAccept={() => setConfirmOpen(true)}
          onDecline={() => setDeclineOpen(true)}
        />
      )}

      {confirmOpen && userGroupId != null && (
        <ConfirmBatchModal
          open={confirmOpen}
          selections={selections}
          userGroupId={userGroupId}
          onCancel={() => setConfirmOpen(false)}
          onCompleted={() => {
            setConfirmOpen(false);
            clearSelection();
          }}
        />
      )}

      {declineOpen && userGroupId != null && (
        <DeclineModal
          open={declineOpen}
          offers={selections.map((selection) => selection.offer)}
          userGroupId={userGroupId}
          onCancel={() => setDeclineOpen(false)}
          onConfirm={() => {
            setDeclineOpen(false);
            clearSelection();
          }}
        />
      )}
    </ContentWrapperCustomerApp>
  );
};
